import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { SlaPause } from "@helpdesk/sla";

import { api } from "./api";
import { queryKeys } from "./queryKeys";
import type { AwayPeriod } from "../types";

/**
 * Who is away, for every clock on screen.
 *
 * A ticket's SLA countdown excludes the time its assignee was at lunch or off
 * for the day, and the SPA recomputes that countdown locally each second (see
 * lib/sla.ts, which explains why). So every row in the queue needs the away
 * periods of whoever owns it -- not the viewer's own.
 *
 * ## Why a context rather than a prop
 *
 * The calendar beside it is threaded down from the page, deliberately: it is
 * fetched once so a two-hundred-row list does not open two hundred
 * subscriptions to the same query. That reasoning is about the *fetch*, not
 * about the prop, and this value is needed one level deeper -- inside
 * `useLiveSla` itself, which every row calls. Threading it would mean a fifth
 * prop through four components that have no other use for it, for a value that
 * is identical in all of them.
 *
 * So it is fetched once here, like the calendar, and read from context by the
 * hook that needs it. A missing provider yields an empty map rather than
 * throwing: the clock is then exactly as right as it was before any of this
 * existed, which is the correct thing for a screen mounted outside the shell.
 */

/** Pauses by agent id. Agents with nothing recorded are absent, not empty. */
export type PauseLookup = ReadonlyMap<number, SlaPause[]>;

const EMPTY: PauseLookup = new Map();

const AwayContext = createContext<PauseLookup>(EMPTY);

/**
 * How often the desk's away periods are re-read.
 *
 * A minute, which is slower than the countdown and far faster than it matters:
 * the only thing this misses is a colleague pressing Lunch, and a clock that
 * keeps ticking for up to a minute afterwards is off by a minute, not by an
 * hour. The agent's *own* press does not wait for this -- that invalidates the
 * query outright.
 */
const REFRESH_MS = 60_000;

export function AwayProvider({ children }: { children: ReactNode }) {
  const { data } = useQuery({
    queryKey: queryKeys.away,
    queryFn: api.away,
    staleTime: REFRESH_MS,
    refetchInterval: REFRESH_MS,
  });

  const value = useMemo(
    () => pauseLookup(data?.periods ?? []),
    [data?.periods],
  );

  return <AwayContext.Provider value={value}>{children}</AwayContext.Provider>;
}

/** The desk's away periods, grouped by agent. */
export function useAwayPauses(): PauseLookup {
  return useContext(AwayContext);
}

/**
 * Group periods by agent, as instants.
 *
 * `endedAt` wins over `endsAt` when it is set, because it means somebody came
 * back early -- the server has already reduced it to the effective end, so
 * this is a straight preference rather than a second min().
 */
export function pauseLookup(periods: readonly AwayPeriod[]): PauseLookup {
  const byUser = new Map<number, SlaPause[]>();

  for (const period of periods) {
    const pause: SlaPause = {
      start: new Date(period.startedAt),
      end: new Date(period.endedAt ?? period.endsAt),
    };
    const existing = byUser.get(period.userId);
    if (existing) existing.push(pause);
    else byUser.set(period.userId, [pause]);
  }

  return byUser;
}
